import { Alert } from "@mui/material";

import { AlertDialog } from "@/shared/components/AlertDialog";
import { RobotoButton } from "@/shared/components/RobotoButton";

export interface SearchIndexConfirmationProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  logCount: number;
}

/**
 * An inline alert shown on the panel when there are many logs and the search
 * index needs to be built for loaded filters.
 */
export function SearchIndexConfirmationAlert({
  open,
  onClose,
  onConfirm,
  logCount,
}: SearchIndexConfirmationProps) {
  if (!open) {
    return null;
  }

  const logCountText = logCount.toLocaleString();

  return (
    <Alert
      severity="warning"
      action={
        <span>
          <RobotoButton
            eventName="LogPanelSearchIndexBuildConfirmed"
            eventProperties={{ logCount }}
            color="inherit"
            size="small"
            onClick={onConfirm}
          >
            Build Index
          </RobotoButton>
          <RobotoButton
            eventName="LogPanelSearchIndexBuildCanceled"
            eventProperties={{ logCount }}
            color="inherit"
            size="small"
            onClick={onClose}
          >
            Cancel
          </RobotoButton>
        </span>
      }
    >
      Building the search index for {logCountText} logs may take a while and
      could cause memory issues.
    </Alert>
  );
}

function buildConfirmationMessage(logCount: number): string {
  return [
    "Searching within the Log Panel requires building a search index in the background.",
    `This panel contains ${logCount.toLocaleString()} logs.`,
    "Building the search index may take a while, and depending on the size of the data, may cause your browser to run out of memory.",
    "We're working on improving this feature to make it more performant for topics with many messages.",
  ].join(" ");
}

/**
 * A modal dialog shown before building a search index when the user clicks to
 * open search with many logs that could cause memory issues.
 */
export function SearchIndexConfirmationDialog({
  open,
  onClose,
  onConfirm,
  logCount,
}: SearchIndexConfirmationProps) {
  return (
    <AlertDialog
      dialogOpen={open}
      handleClose={onClose}
      dialogTitle="Build log search index"
      dialogText={buildConfirmationMessage(logCount)}
      dialogAction={() => {
        return Promise.resolve(onConfirm());
      }}
    />
  );
}
